import { useCallback } from 'react'
import type { ProductKey } from './nav'
import { parseProductsParam } from './products'
import {
  demoSession,
  getSession,
  sessionFromAccess,
  setSession,
  setTransientSession,
  useSession,
} from './session'

/**
 * Sign-in flows. All they do is establish the unified Session (see
 * lib/session.ts); everything downstream reads from that single object.
 *
 * The legacy contract (traffic capture + service extraction):
 *   POST /lc/users/login       -> session (SSID) or OTP challenge
 *   POST /lc/users/verify-otp  -> session (SSID)
 *   GET  /lc/users/access-details -> identity, orgs, scopes, entitlements
 *
 * FINDING (verified 2026-08-20, see docs/inventory/AUTH-DIAGNOSIS.md): on the
 * ap1 demo tenant /lc/users/login is a stub — it returns 400 "Invalid
 * Credentials" for every input, including an empty body, so it never
 * authenticates anyone. access-details, by contrast, returns the real payload
 * for a valid SSID (it appears in the observed traffic capture).
 *
 * signIn() still calls the login endpoint: that is the correct flow, and it is
 * what will work the moment the endpoint does. signInWithSsid() is the escape
 * hatch that works TODAY — paste an SSID from a working browser session and
 * the console builds the session from access-details alone.
 */

export type { Session } from './session'
export { getSession, useSession }

export type LoginResult =
  | { kind: 'session' }
  | { kind: 'otp-required'; challengeId: string }
  | { kind: 'error'; message: string }

/** Live: fetch access-details for an SSID and build the session from it. */
async function establishLive(ssid: string): Promise<LoginResult> {
  const { authApi, client } = await import('./api')
  // Point the client at this SSID for the access-details call.
  const prior = getSession()
  // Transient: in memory so the api-client can read the SSID, but NOT written
  // to sessionStorage. A reload during the access-details call would otherwise
  // restore this cluster-less placeholder as if it were a real session. See
  // setTransientSession.
  setTransientSession({
    ssid,
    mode: 'live',
    user: { userId: '', orgId: '', name: '…', email: '', userType: 'User', isRoot: false, isOrgAdmin: false, isAdmin: false },
    clusters: [],
    establishedAt: Date.now(),
  })
  try {
    void client // client reads ssid via getSession()
    const access = await authApi.accessDetails()
    if (!access.access.length) {
      setSession(prior)
      return { kind: 'error', message: 'This session has no cluster access.' }
    }
    setSession(sessionFromAccess(ssid, access, 'live'))
    return { kind: 'session' }
  } catch (e) {
    setSession(prior)
    // Say plainly that this is NOT a credentials problem. Authentication has
    // already succeeded by the time we get here, and reporting this as a bad
    // password sends people back to retype a password that was always correct.
    const detail = e instanceof Error ? e.message : 'Could not load your access.'
    return { kind: 'error', message: `Signed in, but your access could not be loaded: ${detail}` }
  }
}

/**
 * Demo entitlement narrowing: `?products=fabric,vantage` on the login URL
 * fakes a subset session (requirements §5 step 6; usage documented in
 * docs/product-manifest.md). The subset is baked into the demo session so it
 * survives reload; an unknown id REFUSES the sign-in rather than silently
 * granting everything — the entitlement e2e suite depends on that.
 */
function demoProductNarrowing():
  | { kind: 'ok'; products: ProductKey[] | undefined }
  | { kind: 'error'; message: string } {
  const parsed = parseProductsParam(window.location.search)
  if (!parsed.ok) return { kind: 'error', message: parsed.error }
  return { kind: 'ok', products: parsed.products ?? undefined }
}

export async function signIn(email: string, password: string): Promise<LoginResult> {
  const { getAuthMode, authApi } = await import('./api')
  if (getAuthMode() === 'live') {
    try {
      const res = await authApi.login(email, password)
      if (res.mfa_flag === 'enabled') return { kind: 'otp-required', challengeId: email }
      if (res.SSID) return establishLive(res.SSID)
      return { kind: 'error', message: 'Unexpected response from the login service.' }
    } catch (e) {
      return { kind: 'error', message: e instanceof Error ? e.message : 'Login failed.' }
    }
  }
  // demo
  await new Promise((r) => setTimeout(r, 500))
  if (email.toLowerCase() === 'fail@bloo.io') return { kind: 'error', message: 'Invalid email or password.' }
  if (email.toLowerCase() === 'demo-otp@bloo.io') return { kind: 'otp-required', challengeId: 'demo-challenge' }
  const narrowed = demoProductNarrowing()
  if (narrowed.kind === 'error') return { kind: 'error', message: narrowed.message }
  setSession(demoSession(email, narrowed.products))
  return { kind: 'session' }
}

export async function verifyOtp(challengeId: string, code: string): Promise<LoginResult> {
  const { getAuthMode, authApi } = await import('./api')
  if (getAuthMode() === 'live') {
    try {
      const res = await authApi.verifyOtp(challengeId, code)
      if (res.SSID) return establishLive(res.SSID)
      return { kind: 'error', message: 'Verification failed.' }
    } catch (e) {
      return { kind: 'error', message: e instanceof Error ? e.message : 'Verification failed.' }
    }
  }
  await new Promise((r) => setTimeout(r, 400))
  if (code !== '000000' && code.length === 6) {
    const narrowed = demoProductNarrowing()
    if (narrowed.kind === 'error') return { kind: 'error', message: narrowed.message }
    setSession(demoSession('demo-otp@bloo.io', narrowed.products))
    return { kind: 'session' }
  }
  return { kind: 'error', message: 'That code is not valid. 000000 always fails in demo mode.' }
}

/** Establish a live session directly from an SSID (existing-session path). */
export async function signInWithSsid(ssid: string): Promise<LoginResult> {
  const { setAuthMode } = await import('./api')
  setAuthMode('live')
  return establishLive(ssid)
}

export function signOut() {
  setSession(null)
}

export function signOutOnUnauthorized() {
  setSession(null)
  // Path, not hash: the router is path-based, so the hash is always '' and a
  // hash test would hard-reload the login page onto itself — wiping the very
  // error message the caller was about to show.
  if (!window.location.pathname.startsWith('/auth/login')) window.location.assign('/auth/login')
}

export function useSignOut(): () => void {
  return useCallback(() => signOut(), [])
}
